import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ConditionPanel,
  ConditionGrid,
  ConditionBlock,
  ConditionLabel,
  ConditionChipRow,
  ConditionDateRow,
  ConditionDateInput,
  ConditionNumberInput,
  ConditionHint,
  ConditionFooter,
  ConditionResetButton,
  ConditionApplyButton,
  MemberFilterChip,
} from './MemberGroupModalStyled';

// 회원목록 상세검색 필터와 같은 조건·같은 서버 파라미터를 쓴다.
// UI 만 모달 폭에 맞춰 2열로 다시 그린 것이다 (원본은 최소 1090px 고정 3열이라 모달에 안 들어간다).
export const MEMBER_TYPE_OPTIONS = [
  { value: 'ACTIVE', label: '유효회원' },
  { value: 'SCHEDULED', label: '사용예정' },
  { value: 'EXPIRING_SOON', label: '만료임박' },
  { value: 'STOPPED', label: '중지회원' },
  { value: 'UNPAID', label: '미수회원' },
  { value: 'LOCKER_EXPIRING', label: '라커만료' },
  { value: 'EXPIRED', label: '만료회원' },
];

const TICKET_TYPE_OPTIONS = [
  { value: 'COURSE_TICKET', label: '수강권' },
  { value: 'COURSE_PASS', label: '이용권' },
  { value: 'TRIAL_TICKET', label: '일일권' },
];

const CLASS_TYPE_OPTIONS = [
  { value: 'PRIVATE_LESSON', label: '개인레슨' },
  { value: 'GROUP_LESSON', label: '그룹레슨' },
];

const GENDER_OPTIONS = [
  { value: 'MALE', label: '남자' },
  { value: 'FEMALE', label: '여자' },
];

const AGE_RANGE_OPTIONS = [
  { value: 'UNDER_30', label: '30세 미만' },
  { value: 'AGE_30_40', label: '30~40세' },
  { value: 'AGE_40_50', label: '40~50세' },
  { value: 'AGE_50_60', label: '50~60세' },
  { value: 'OVER_60', label: '60세 이상' },
];

const SMS_OPTIONS = [
  { value: true, label: '동의' },
  { value: false, label: '미동의' },
];

const REMAINING_OPTIONS = [
  { value: 'UNDER_3', label: '3회 이하' },
  { value: 'UNDER_5', label: '5회 이하' },
  { value: 'CUSTOM', label: '직접 입력' },
];

const SPORT_TYPE_LABELS = {
  FITNESS: '피트니스 / P.T',
  PILATES: '필라테스',
  BARRE_PILATES: '바레 필라테스',
  GOLF: '골프',
  YOGA: '요가',
  TANNING: '태닝',
  STRETCHING: '스트레칭',
};

// 회원목록 조회가 받는 키 그대로. 빈 배열/빈 문자열이면 조건 없음이다.
export const EMPTY_CONDITIONS = {
  memberTypes: [],
  exerciseTypes: [],
  ticketTypes: [],
  classTypes: [],
  genders: [],
  ageRanges: [],
  smsReceiveList: [],
  remainingCounts: '',
  customRemainingCount: '',
  ticketExpireDateFrom: '',
  ticketExpireDateTo: '',
  lockerExpireDateFrom: '',
  lockerExpireDateTo: '',
  memberStopDateFrom: '',
  memberStopDateTo: '',
};

// 백엔드는 시작·종료가 둘 다 있어야 날짜 조건을 건다(한쪽만 오면 조용히 무시). 그래서 한 쌍이 다 찼을 때만 센다.
const rangeComplete = (from, to) => !!from && !!to;

// 아이콘에 표시할 '적용 개수'. 날짜 범위는 시작/종료를 묶어 1개로 센다.
export const countConditions = conditions => {
  const lists = ['memberTypes', 'exerciseTypes', 'ticketTypes', 'classTypes', 'genders', 'ageRanges', 'smsReceiveList'];
  let count = lists.filter(key => (conditions[key] || []).length > 0).length;
  if (conditions.remainingCounts) count += 1;
  if (rangeComplete(conditions.ticketExpireDateFrom, conditions.ticketExpireDateTo)) count += 1;
  if (rangeComplete(conditions.lockerExpireDateFrom, conditions.lockerExpireDateTo)) count += 1;
  if (rangeComplete(conditions.memberStopDateFrom, conditions.memberStopDateTo)) count += 1;
  return count;
};

const MemberGroupConditionPanel = ({ draft, setDraft, sportTypes = [], onApply, onReset }) => {
  const { t } = useTranslation();

  const toggleInList = (key, value) =>
    setDraft(prev => {
      const list = prev[key] || [];
      const next = list.includes(value) ? list.filter(item => item !== value) : [...list, value];
      // 회원 구분을 다 풀면 잠기는 만료일 값도 함께 비운다 (남아 있으면 잠긴 채로 조회에 나간다)
      if (key === 'memberTypes' && next.length === 0) {
        return { ...prev, memberTypes: next, ticketExpireDateFrom: '', ticketExpireDateTo: '' };
      }
      return { ...prev, [key]: next };
    });

  // 연령대·수신동의는 회원목록도 한 번에 하나만 고른다. 같은 값을 누르면 해제된다.
  const pickSingle = (key, value) =>
    setDraft(prev => ({ ...prev, [key]: (prev[key] || [])[0] === value ? [] : [value] }));

  const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  const renderList = (key, options) => (
    <ConditionChipRow>
      {options.map(option => (
        <MemberFilterChip
          key={String(option.value)}
          isActive={(draft[key] || []).includes(option.value)}
          onClick={() => toggleInList(key, option.value)}>
          {t(option.label)}
        </MemberFilterChip>
      ))}
    </ConditionChipRow>
  );

  const renderSingle = (key, options) => (
    <ConditionChipRow>
      {options.map(option => (
        <MemberFilterChip
          key={String(option.value)}
          isActive={(draft[key] || [])[0] === option.value}
          onClick={() => pickSingle(key, option.value)}>
          {t(option.label)}
        </MemberFilterChip>
      ))}
    </ConditionChipRow>
  );

  // 회원 만료일은 회원 구분에 따라 뜻이 달라진다. 회원목록도 구분을 고르기 전에는 잠가 둔다.
  // (EXPIRED 를 고르면 '만료된 수강권의 만료일', 아니면 '활성 수강권의 만료예정일' 로 매칭된다)
  const memberTypeChosen = (draft.memberTypes || []).length > 0;

  const renderDateRange = (fromKey, toKey, disabled = false) => {
    const from = draft[fromKey] || '';
    const to = draft[toKey] || '';
    const halfFilled = (!!from) !== (!!to);
    return (
      <>
        <ConditionDateRow>
          <ConditionDateInput
            type="date"
            value={from}
            disabled={disabled}
            onChange={e => setField(fromKey, e.target.value)}
          />
          <span style={{ fontSize: '12px', color: '#8A939C' }}>~</span>
          <ConditionDateInput
            type="date"
            value={to}
            min={from || undefined}
            disabled={disabled}
            onChange={e => setField(toKey, e.target.value)}
          />
        </ConditionDateRow>
        {halfFilled && (
          <ConditionHint>{t('시작일과 종료일을 모두 넣어야 적용됩니다')}</ConditionHint>
        )}
      </>
    );
  };

  return (
    <ConditionPanel>
      <ConditionGrid>
        <ConditionBlock>
          <ConditionLabel>{t('회원 구분')}</ConditionLabel>
          {renderList('memberTypes', MEMBER_TYPE_OPTIONS)}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('운동 종목')}</ConditionLabel>
          {renderList(
            'exerciseTypes',
            sportTypes.map(type => ({ value: type, label: SPORT_TYPE_LABELS[type] || type })),
          )}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('회원권 유형')}</ConditionLabel>
          {renderList('ticketTypes', TICKET_TYPE_OPTIONS)}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('수강권 유형')}</ConditionLabel>
          {renderList('classTypes', CLASS_TYPE_OPTIONS)}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('성별')}</ConditionLabel>
          {renderList('genders', GENDER_OPTIONS)}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('연령대')}</ConditionLabel>
          {renderSingle('ageRanges', AGE_RANGE_OPTIONS)}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('문자 수신 동의')}</ConditionLabel>
          {renderSingle('smsReceiveList', SMS_OPTIONS)}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('잔여 횟수')}</ConditionLabel>
          <ConditionChipRow>
            {REMAINING_OPTIONS.map(option => (
              <MemberFilterChip
                key={option.value}
                isActive={draft.remainingCounts === option.value}
                onClick={() =>
                  setDraft(prev => ({
                    ...prev,
                    remainingCounts: prev.remainingCounts === option.value ? '' : option.value,
                    customRemainingCount:
                      option.value === 'CUSTOM' ? prev.customRemainingCount : '',
                  }))
                }>
                {t(option.label)}
              </MemberFilterChip>
            ))}
            {draft.remainingCounts === 'CUSTOM' && (
              <ConditionNumberInput
                type="number"
                min="0"
                value={draft.customRemainingCount || ''}
                placeholder={t('횟수')}
                onChange={e => setField('customRemainingCount', e.target.value)}
              />
            )}
          </ConditionChipRow>
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>
            {memberTypeChosen ? t('회원 만료일') : `${t('회원 만료일')} (${t('회원 구분 선택 시')})`}
          </ConditionLabel>
          {renderDateRange('ticketExpireDateFrom', 'ticketExpireDateTo', !memberTypeChosen)}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('락커 만료일')}</ConditionLabel>
          {renderDateRange('lockerExpireDateFrom', 'lockerExpireDateTo')}
        </ConditionBlock>
        <ConditionBlock>
          <ConditionLabel>{t('회원 중지 종료일')}</ConditionLabel>
          {renderDateRange('memberStopDateFrom', 'memberStopDateTo')}
        </ConditionBlock>
      </ConditionGrid>

      <ConditionFooter>
        <ConditionResetButton onClick={onReset}>{t('초기화')}</ConditionResetButton>
        <ConditionApplyButton onClick={onApply}>{t('적용')}</ConditionApplyButton>
      </ConditionFooter>
    </ConditionPanel>
  );
};

export default MemberGroupConditionPanel;
